import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then((r) => { setItems(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => { await api.post("/notifications/read").catch(() => {}); load(); };
  const openNotif = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.shipment_id) navigate(`/shipments/${n.shipment_id}`);
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell"
                className="relative h-9 w-9 flex items-center justify-center rounded-full hover:bg-[#F0F2F0] transition-colors">
          <Bell className="h-5 w-5 text-[#111411]" />
          {unread > 0 && (
            <span data-testid="notif-unread"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C24127] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-heading font-bold text-[#111411]">Notifications</p>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} data-testid="notif-mark-all" className="gap-1.5 text-[#0B3D3D] h-7">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10" data-testid="notif-empty">You&apos;re all caught up.</p>
          ) : (
            items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openNotif(n)}
                      className={`w-full text-left px-4 py-3 border-b border-border hover:bg-[#F9F9F8] transition-colors ${n.read ? "" : "bg-[#F3F8F3]"}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#0B3D3D] shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#111411]">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
